import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { twMerge } from 'tailwind-merge';
import { ConversationMessageKey } from 'flow/types/TicketEventData';
import { TicketKey } from 'flow/types/EntityKeys';
import {
    selectEventDirection,
    selectEventTimestamp,
    selectMessageDeliveryStatusIsSending,
} from 'flow/selectors/TicketEventSelectors';
import useAppContext from 'flow/hooks/useAppContext';
import MessageItemSent from 'flow/components/message/MessageItemSent';
import MessageSender from 'flow/components/tickets/MessageSender';
import SenderAvatarExternalService from 'flow/components/tickets/event-item/Messages/SenderAvatarExternalService';
import DeliveryStatus from 'flow/components/tickets/event-item/Messages/DeliveryStatus';
import MessageStyled from 'flow/components/tickets/event-item/Messages/MessageStyled';

interface Props {
    ticketKey: TicketKey;
    messageKey: ConversationMessageKey;
}

const MessageWhatsAppTemplateV2 = ({ ticketKey, messageKey }: Props) => {
    const { i18next } = useAppContext();

    const direction = useAppSelector((state) =>
        selectEventDirection(state, ticketKey, messageKey),
    );
    const timestamp = useAppSelector((state) =>
        selectEventTimestamp(state, ticketKey, messageKey),
    );
    const deliveryStatusIsSending = useAppSelector((state) =>
        selectMessageDeliveryStatusIsSending(state, ticketKey, messageKey),
    );

    return (
        <div
            className={twMerge(
                'bg-gray-150 flex flex-col gap-1 rounded-xl p-3',
                deliveryStatusIsSending && 'animate-fade opacity-50',
            )}
        >
            <div className="flex h-6 items-center gap-2 text-sm">
                <SenderAvatarExternalService
                    ticketKey={ticketKey}
                    messageKey={messageKey}
                    size="small"
                />
                <MessageSender ticketKey={ticketKey} messageKey={messageKey} />
                <span className="text-sm text-gray-500">
                    {i18next.t('tickets.whatsapp.whatsapp_template')}
                </span>
                <span className="ms-auto text-sm text-gray-500">
                    <MessageItemSent timeString={timestamp} />
                </span>
            </div>
            <div className="ps-8 wrap-break-word whitespace-pre-wrap">
                <div className="min-w-0 text-base wrap-break-word text-gray-800 no-underline">
                    <MessageStyled
                        ticketKey={ticketKey}
                        messageKey={messageKey}
                    />
                </div>
                {direction === 'OUTGOING' && (
                    <DeliveryStatus
                        messageKey={messageKey}
                        ticketKey={ticketKey}
                    />
                )}
            </div>
        </div>
    );
};

export default React.memo(MessageWhatsAppTemplateV2);
